import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * The single page shell for the whole app.
 *
 * Before this existed the 46 screens carried 15 different root paddings and
 * widths (p-2 through p-8, max-w-4xl through max-w-[1800px], several with
 * none), so no two screens lined up with each other. Everything now sits on
 * one width and one gutter.
 *
 * `width="narrow"` is for single-column reading and form screens (policy,
 * profile, new trip); `wide` is for tables that genuinely need the room.
 */
const WIDTHS = {
  narrow: 'max-w-3xl',
  default: 'max-w-page',
  wide: 'max-w-[1800px]',
  full: 'max-w-none',
};

const PageContainer = ({
  className,
  width = 'default',
  children,
  ref,
  ...props
}) => (
  <div
    ref={ref}
    className={cn(
      'mx-auto w-full px-4 py-5 md:px-6 md:py-6 lg:px-8',
      WIDTHS[width] ?? WIDTHS.default,
      className,
    )}
    {...props}
  >
    {children}
  </div>
);
PageContainer.displayName = 'PageContainer';

/**
 * A titled block within a page. Keeps vertical rhythm consistent between
 * sections instead of each screen inventing its own margin.
 */
const PageSection = ({ title, description, actions, className, children, ref, ...props }) => (
  <section ref={ref} className={cn('space-y-3', className)} {...props}>
    {(title || actions) && (
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-0.5">
          {title && <h2>{title}</h2>}
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
    )}
    {children}
  </section>
);
PageSection.displayName = 'PageSection';

export { PageContainer, PageSection };
